import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { map, Subscription, tap } from 'rxjs';
import {
  Filters,
  FnCaricamentoDati,
  SortBy,
} from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import {
  BottoniListaEvent,
  Colonna,
  Filtri,
  ListaTabellareComponent,
} from 'src/app/components/lista-tabellare/lista-tabellare.component';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import { Azienda, AziendaService } from 'src/app/services/azienda/azienda.service';
import { BilancioService, ConfigBilanci } from 'src/app/services/bilancio/bilancio.service';
import { LicenzeService } from 'src/app/services/licenze/licenze.service';
import { SettoriService } from 'src/app/services/settori/settori.service';
import { TemaService } from 'src/app/services/tema/tema.service';
import { UtenteService } from 'src/app/services/utente/utente.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { DialogCreaBilancioSostenibilitaComponent } from './dialog/dialog-crea-bilancio-sostenibilita/dialog-crea-bilancio-sostenibilita.component';

@Component({
  selector: 'app-bilancio-sostebilita',
  templateUrl: './bilancio-sostebilita.component.html',
  styleUrls: ['./bilancio-sostebilita.component.scss'],
  standalone: false,
})
export class BilancioSostebilitaComponent implements AfterViewInit {
  @ViewChild('tabella') tabella!: ListaTabellareComponent;
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;
  private azienda: Azienda | undefined = undefined;
  public isUtenteGo: boolean = false;
  private configBilanci: ConfigBilanci | undefined = undefined;
  private nrBilanciAzienda: number = 0;
  public disableNuovo = true;
  public filtri: Filtri[] = [
    {
      titolo: 'Anno',
      forControlName: 'anno',
      input: 'text',
    },
    {
      forControlName: 'settori',
      input: 'multiple-option',
      titolo: 'Settore',
      fnDatiOption: () =>
        this.settoriService.getSettori(0, 1000).pipe(map((value) => value?.content || [])),
      descValueOption: 'titolo',
      idValueOption: 'id',
      riempi: true,
      larghezza: 1,
    },
  ];

  public colonne: { [key: string]: Colonna } = {
    anno: {
      title: 'Anno',
      value: 'anno',
    },
    settore: {
      title: 'Settore',
      value: (record: any) => (record?.settori ? record?.settori[0]?.titolo : ''),
    },
    risultatiImpegni: {
      title: 'Impegni',
      value: (record: any) =>
        (record?.compilazioni?.impegni?.compilati || 0) +
        '/' +
        (record?.compilazioni?.impegni?.totali || 0),
    },
    risultatiAzioni: {
      title: 'Azioni',
      value: (record: any) =>
        (record?.compilazioni?.azioni?.compilati || 0) +
        '/' +
        (record?.compilazioni?.azioni?.totali || 0),
    },
    risultatiKpi: {
      title: 'Kpi',
      value: (record: any) =>
        (record?.compilazioni?.kpi?.compilati || 0) +
        '/' +
        (record?.compilazioni?.kpi?.totali || 0),
    },
    dataUltimaModifica: {
      title: 'Ultima Modifica',
      value: (record: any) => record?.dataUltimaModifica ?? '',
    },
    dataCreazione: {
      title: 'Data Creazione',
      value: (record: any) => record?.dataCreazione ?? '',
    },
    stato: this.utilityService.getColonnaStato(),

    modifica: {
      type: 'button',
      buttonIcon: (record: any) => (this.isPermessoSoloRead(record) ? 'visibility' : 'edit'),
      title: (record: any) => (this.isPermessoSoloRead(record) ? 'Visualizza' : 'Modifica'),
      buttonId: 'modifica',
      buttonMostraSempre: true,
      sortable: false,
    },
    elimina: {
      ...this.utilityService.getColonnaElimina(),
      nascondiButton: this.nascondiBottoneElimina(),
    },
    elimina_by_developer: {
      type: 'button',
      buttonIcon: 'delete',
      title: 'Elimina by Developer',
      buttonId: 'elimina_by_developer',
      buttonMostraSempre: false,
      sortable: false,
      nascondiButton: (record: any) => !this.isDeveloper || record.stato === 'BOZZA',
    },
  };

  get isDeveloper() {
    return !!this.utenteService.infoUtente?.isDeveloper;
  }

  private _subAziendaCorrente: Subscription | undefined;
  private _subTema: Subscription | undefined;

  constructor(
    public dialog: MatDialog,
    private readonly utilityService: UtilityService,
    private readonly aziendaService: AziendaService,
    private readonly bilancioService: BilancioService,
    public utenteService: UtenteService,
    private readonly licenzeService: LicenzeService,
    private readonly temaService: TemaService,
    private readonly settoriService: SettoriService,
  ) {}

  nascondiBottoneElimina() {
    if (this.utenteService.infoUtente?.isDeveloper) {
      return false; //non nascondere
    }
    if (this.utenteService.isPermessoAttivo('DELETE_BILANCIO')) {
      return false;
    }
    return true; //nascondere
  }

  isPermessoSoloRead(record: any) {
    const isEdit = this.utenteService.isPermessoAttivo('EDIT_BILANCIO');
    const isCreate = this.utenteService.isPermessoAttivo('CREATE_BILANCIO');
    const statoArchiviato = record.stato === 'ARCHIVIATO';
    return (!isEdit && !isCreate) || statoArchiviato;
  }

  ngAfterViewInit() {
    this._subAziendaCorrente = this.aziendaService.aziendaCorrente.subscribe((azienda) => {
      let cambiato = false;

      if (this.azienda && azienda && this.azienda.id !== azienda.id) {
        cambiato = true;
      }

      this.azienda = azienda;

      if (cambiato) {
        this.tabella?.caricaDati();
      }
    });

    this._subTema = this.temaService.temaUpdated.subscribe((tipoInstallazione) => {
      if (tipoInstallazione === 'SOSTENIBILE_GO') {
        this.isUtenteGo = true;
      } else {
        this.isUtenteGo = false;
      }
    });

    this.bilancioService.getConfigBilanci().subscribe({
      next: (esito) => {
        this.configBilanci = esito;
      },
      error: (er) => {
        console.error(er);
      },
    });
  }

  ngOnDestroy(): void {
    this._subAziendaCorrente?.unsubscribe();
    this._subTema?.unsubscribe();
  }

  public fnCaricamentoDati: FnCaricamentoDati = (
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) => {
    return this.bilancioService.getBilancio(page, pageSize, ricerca, filters, sortBy).pipe(
      tap((result) => {
        this.nrBilanciAzienda = result?.content?.length;
        if (this.nrBilanciAzienda >= 1 && this.isUtenteGo) {
          this.disableNuovo = true;
        } else {
          this.disableNuovo = false;
        }
      }),
    );
  };

  /**
   * Metodo di modifica o creazione nuova bilancio
   * @param record
   */
  crea(record?: any) {
    if (!record) {
      this.spinnerOver.show();

      this.licenzeService.getUserHasLicense(this.isUtenteGo ? 'Report' : 'Bilancio').subscribe({
        next: (esito) => {
          this.spinnerOver.hide();
          this.apriDialogCreaBilancio(record);
        },
        error: (err) => {
          this.spinnerOver.hide();

          this.utilityService.openDialog({
            titolo: 'Attenzione',
            descrizione: err?.error?.message,
            bottoni: [{ nome_btn: 'Ok' }],
          });
        },
      });
    } else {
      this.apriDialogCreaBilancio(record);
    }
  }

  private apriDialogCreaBilancio(record: any) {
    const dialogCreaBilancio = this.dialog.open(DialogCreaBilancioSostenibilitaComponent, {
      data: {
        bilancio: record,
        configBilancio: this.configBilanci,
      },
      panelClass: ['dialog-container-scrollbar'],
      disableClose: false,
      width: '80%',
      height: '99%',
      autoFocus: false,
    });

    dialogCreaBilancio.afterClosed().subscribe((result) => {
      // Ricarica i dati della tabella dopo la chiusura del dialogo
      this.tabella.caricaDati();
    });
  }

  public bottoniListaClick(event: BottoniListaEvent) {
    switch (event.id) {
      case 'modifica':
        this.crea(event.data);
        break;
      case 'elimina':
      case 'archivia':
      case 'disarchivia':
        this._elimina(event.data, false);
        break;
      case 'elimina_by_developer':
        this._elimina(event.data, true);
        break;

      default:
        console.warn('ATTENZIONE: bottone ' + event.id + ' non riconosciuto');
    }
  }

  /**
   * Gestisce l'eliminazione di un modulo tramite il service `utilityService`.
   * Mostra lo spinner durante l'operazione e aggiorna la tabella al termine.
   *
   * @async
   * @param {any} modulo - L'oggetto modulo da eliminare. Deve contenere almeno l'ID e lo stato.
   *
   */
  async _elimina(modulo: any, deleteByDeveloper: boolean) {
    this.spinnerOver.show();

    const success = await this.utilityService.eliminaModulo(
      modulo,
      this.bilancioService,
      deleteByDeveloper,
    );

    this.spinnerOver.hide();

    if (success) {
      this.tabella.caricaDati();
    } else {
      console.log('Operazione annullata o non riuscita');
    }
  }
}
